import { useState } from 'react';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  id?: string;
  className?: string;
}

/**
 * Свободный ввод тегов (AK-49, founder-решение: теги без преднабора).
 * Enter или запятая добавляют тег; × или Backspace на пустом инпуте удаляют.
 */
export function TagInput({
  value,
  onChange,
  placeholder = 'введите тег и Enter…',
  id,
  className,
}: TagInputProps) {
  const [draft, setDraft] = useState('');

  const addTag = (raw: string) => {
    const tag = raw.trim();
    if (!tag) return;
    if (value.includes(tag)) {
      setDraft('');
      return;
    }
    onChange([...value, tag]);
    setDraft('');
  };

  const removeTag = (tag: string) => onChange(value.filter((t) => t !== tag));

  return (
    <div
      className={cn(
        'flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5',
        className,
      )}
      data-testid="tag-input"
    >
      {value.map((tag) => (
        <Badge key={tag} variant="primary" className="gap-1">
          {tag}
          <button
            type="button"
            aria-label={`Удалить тег ${tag}`}
            onClick={() => removeTag(tag)}
            className="rounded-full hover:bg-primary/20"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      <Input
        id={id}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addTag(draft);
          } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
            removeTag(value[value.length - 1]);
          }
        }}
        onBlur={() => addTag(draft)}
        placeholder={value.length === 0 ? placeholder : ''}
        className="h-6 flex-1 border-0 px-1 shadow-none focus-visible:ring-0"
      />
    </div>
  );
}
